import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { detectUserTimezone, getTimezoneHeaders } from '../utils/timezoneUtils';
import { apiUrl, fetchWithFallback } from '@/config/api';

export type VerificationChannel = 'email' | 'sms';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'tailor' | 'customer' | 'admin';
  shopId?: string;
  phone?: string;
  lastLogin: string;
  timezone?: string;
  tier?: 'free' | 'basic' | 'premium' | 'enterprise';
  emailVerifiedAt?: string | null;
  phoneVerifiedAt?: string | null;
  preferredVerificationMethod?: VerificationChannel | null;
}

export interface VerificationState {
  needsVerification: boolean;
  showPrompt: boolean;
  channel: VerificationChannel | null;
  isSending: boolean;
  isVerifying: boolean;
  lastSentTo: string | null;
  expiresAt: string | null;
  error: string | null;
}

export interface VerificationActionResult {
  success: boolean;
  message?: string;
}

const DEFAULT_VERIFICATION_STATE: VerificationState = {
  needsVerification: false,
  showPrompt: false,
  channel: null,
  isSending: false,
  isVerifying: false,
  lastSentTo: null,
  expiresAt: null,
  error: null,
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (userData: RegisterData) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message: string; errorCode?: string; suggestion?: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
  verifyResetToken: (token: string) => Promise<boolean>;
  clearPasswordChangeFlag: () => void;
  verificationState: VerificationState;
  requestVerification: (channel: VerificationChannel) => Promise<VerificationActionResult>;
  verifyCode: (channel: VerificationChannel, code: string) => Promise<VerificationActionResult>;
  dismissVerificationPrompt: () => void;
  resetVerificationError: () => void;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  role: 'tailor' | 'customer' | 'admin';
  shopName?: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [verificationState, setVerificationState] = useState<VerificationState>(DEFAULT_VERIFICATION_STATE);

  const resetVerificationState = useCallback(() => {
    setVerificationState(DEFAULT_VERIFICATION_STATE);
  }, []);

  const evaluateVerification = useCallback((candidateUser: User | null) => {
    if (!candidateUser) {
      resetVerificationState();
      return;
    }

    const emailVerified = Boolean(candidateUser.emailVerifiedAt);
    const phoneVerified = Boolean(candidateUser.phoneVerifiedAt);
    const needsVerification = !(emailVerified || phoneVerified);

    if (!needsVerification) {
      resetVerificationState();
      return;
    }

    const hasEmail = Boolean(candidateUser.email);
    const hasPhone = Boolean(candidateUser.phone);

    let channel: VerificationChannel | null = null;
    if (candidateUser.preferredVerificationMethod === 'sms' && hasPhone) {
      channel = 'sms';
    } else if (candidateUser.preferredVerificationMethod === 'email' && hasEmail) {
      channel = 'email';
    } else if (hasEmail) {
      channel = 'email';
    } else if (hasPhone) {
      channel = 'sms';
    }

    setVerificationState(prev => ({
      ...DEFAULT_VERIFICATION_STATE,
      needsVerification: true,
      showPrompt: true,
      channel,
      lastSentTo: prev.channel === channel ? prev.lastSentTo : null,
      expiresAt: prev.channel === channel ? prev.expiresAt : null,
    }));
  }, [resetVerificationState]);

  // Helper function to check if JWT token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      // Decode JWT payload (base64url decode)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return exp < Date.now();
    } catch (_error) {
      // If we can't decode the token, consider it expired
      return true;
    }
  };

  useEffect(() => {
    // Check for existing authentication on app load
    const storedToken = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    
    if (storedToken && userData) {
      try {
        // Check if token is expired before using it
        if (isTokenExpired(storedToken)) {
          console.log('🔄 Token expired, clearing auth state');
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          resetVerificationState();
        } else {
          const parsedUser = JSON.parse(userData);
          if (!parsedUser.tier) {
            parsedUser.tier = 'free';
          }
          setUser(parsedUser);
          setToken(storedToken);
          evaluateVerification(parsedUser);
        }
      } catch (_error) {
        console.log('🔄 Invalid auth data, clearing storage');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
         resetVerificationState();
      }
    }
    else {
      resetVerificationState();
    }
    setIsLoading(false);
  }, [evaluateVerification, resetVerificationState]);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      const timezoneHeaders = getTimezoneHeaders();
      const response = await fetchWithFallback('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...timezoneHeaders,
        },
        body: JSON.stringify({
          email: email,
          password: password
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Full login API response:', data);
        console.log('🔍 User tier from API:', data.user.tier);
        
        const user: User = {
          id: data.user.id.toString(),
          email: data.user.email,
          name: data.user.full_name,
          role: data.user.role as 'tailor' | 'customer' | 'admin',
          tier: (data.user.tier ?? 'free') as User['tier'],
          timezone: data.user.timezone || detectUserTimezone(),
          lastLogin: new Date().toISOString(),
          phone: data.user.phone_number ?? undefined,
          emailVerifiedAt: data.user.email_verified_at ?? null,
          phoneVerifiedAt: data.user.phone_verified_at ?? null,
          preferredVerificationMethod: data.user.preferred_verification_method ?? null,
        };
        
        console.log('🔍 Constructed user object:', user);
        
        localStorage.setItem('authToken', data.access_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        setUser(user);
        setToken(data.access_token);
        evaluateVerification(user);
        
        // Preload Google Maps API for faster address autocomplete
        const googleMapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (googleMapsKey) {
          import('../utils/googleMapsLoader').then(({ loadGoogleMapsAPI }) => {
            loadGoogleMapsAPI(googleMapsKey).catch((error) => {
              console.warn('Failed to preload Google Maps API:', error);
            });
          });
        }
        
        // Check if user needs to change password
        try {
          const passwordCheckResponse = await fetchWithFallback('/api/auth/password-change-required', {
            headers: {
              'Authorization': `Bearer ${data.access_token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (passwordCheckResponse.ok) {
            const passwordData = await passwordCheckResponse.json();
            if (passwordData.must_change_password) {
              // Store flag to show password change modal
              localStorage.setItem('mustChangePassword', 'true');
            }
          }
        } catch (_error) {
          console.warn('Could not check password change requirement:', error);
        }
        
        // Store redirect path for role-based navigation
        localStorage.setItem('loginRedirect', user.role === 'customer' ? '/customer/dashboard' : 
                           user.role === 'tailor' ? '/business/dashboard' : 
                           user.role === 'admin' ? '/admin/dashboard' : '/customer/dashboard');
        
        return true;
      } else {
        const errorData = await response.json();
        console.error('Login failed:', errorData);
        return false;
      }
    } catch (_error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearPasswordChangeFlag = () => {
    localStorage.removeItem('mustChangePassword');
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    resetVerificationState();
  };

  const requestVerification = async (channel: VerificationChannel): Promise<VerificationActionResult> => {
    if (!user) {
      return { success: false, message: 'You must be logged in to verify your account.' };
    }

    const contact = channel === 'email' ? user.email : user.phone;
    if (!contact) {
      const message = channel === 'email'
        ? 'Email address is not available.'
        : 'Phone number is not available.';
      setVerificationState(prev => ({
        ...prev,
        error: message,
        showPrompt: true,
        channel,
      }));
      return { success: false, message };
    }

    setVerificationState(prev => ({
      ...prev,
      isSending: true,
      error: null,
      showPrompt: true,
      channel,
      needsVerification: true,
    }));

    try {
      const response = await fetch(apiUrl('/api/send-otp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          contact,
          contact_type: channel,
          user_name: user.name ?? 'User',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.detail || errorData.error || 'Unable to send verification code.';
        setVerificationState(prev => ({
          ...prev,
          isSending: false,
          error: message,
          channel,
        }));
        return { success: false, message };
      }

      const data = await response.json();
      setVerificationState(prev => ({
        ...prev,
        isSending: false,
        needsVerification: true,
        showPrompt: true,
        channel,
        lastSentTo: contact,
        expiresAt: data.expires_at ?? null,
        error: null,
      }));

      return {
        success: true,
        message: data.message ?? 'Verification code sent.',
      };
    } catch (_error) {
      const message = 'Unable to send verification code. Please try again.';
      setVerificationState(prev => ({
        ...prev,
        isSending: false,
        error: message,
        channel,
      }));
      return { success: false, message };
    }
  };

  const verifyCode = async (channel: VerificationChannel, code: string): Promise<VerificationActionResult> => {
    if (!user) {
      return { success: false, message: 'You must be logged in to verify your account.' };
    }

    const contact = channel === 'email' ? user.email : user.phone;
    if (!contact) {
      const message = channel === 'email'
        ? 'Email address is not available.'
        : 'Phone number is not available.';
      setVerificationState(prev => ({
        ...prev,
        error: message,
        showPrompt: true,
        channel,
      }));
      return { success: false, message };
    }

    setVerificationState(prev => ({
      ...prev,
      isVerifying: true,
      error: null,
      channel,
    }));

    try {
      const response = await fetch(apiUrl('/api/verify-otp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          contact,
          contact_type: channel,
          otp_code: code,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.detail || errorData.error || 'Invalid verification code.';
        setVerificationState(prev => ({
          ...prev,
          isVerifying: false,
          error: message,
          channel,
        }));
        return { success: false, message };
      }

      const data = await response.json();
      const verifiedAt = data.verified_at ?? new Date().toISOString();
      const updatedUser: User = {
        ...user,
        emailVerifiedAt: channel === 'email' ? verifiedAt : user.emailVerifiedAt,
        phoneVerifiedAt: channel === 'sms' ? verifiedAt : user.phoneVerifiedAt,
        preferredVerificationMethod: (data.verified_channel as VerificationChannel | undefined) ?? channel,
      };

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      const stillNeedsVerification = !(Boolean(updatedUser.emailVerifiedAt) || Boolean(updatedUser.phoneVerifiedAt));

      if (stillNeedsVerification) {
        setVerificationState(prev => ({
          ...prev,
          isVerifying: false,
          needsVerification: true,
          showPrompt: true,
          error: null,
          lastSentTo: prev.lastSentTo,
          expiresAt: null,
        }));
      } else {
        resetVerificationState();
      }

      return {
        success: true,
        message: data.message ?? 'Verification successful.',
      };
    } catch (_error) {
      const message = 'Verification failed. Please try again.';
      setVerificationState(prev => ({
        ...prev,
        isVerifying: false,
        error: message,
        channel,
      }));
      return { success: false, message };
    }
  };

  const dismissVerificationPrompt = () => {
    setVerificationState(prev => ({
      ...prev,
      showPrompt: false,
    }));
  };

  const resetVerificationError = () => {
    setVerificationState(prev => ({
      ...prev,
      error: null,
    }));
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      console.log('🔍 Starting registration for:', userData.email);
      const response = await fetch(apiUrl('/api/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          name: userData.name,
          role: userData.role,
          phone: userData.phone
        }),
      });

      console.log('🔍 Registration response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Registration successful, data:', data);
        
        const user: User = {
          id: data.user.id.toString(),
          name: data.user.full_name,
          email: data.user.email,
          role: data.user.role as 'tailor' | 'customer' | 'admin',
          tier: 'free',
          shopId: userData.role === 'tailor' ? `shop_${Date.now()}` : undefined,
          phone: userData.phone,
          lastLogin: new Date().toISOString(),
          emailVerifiedAt: data.user.email_verified_at ?? null,
          phoneVerifiedAt: data.user.phone_verified_at ?? null,
          preferredVerificationMethod: data.user.preferred_verification_method ?? null,
        };

        console.log('🔍 Attempting auto-login after registration...');
        // For registration, we need to login the user after successful registration
        const loginSuccess = await login(userData.email, userData.password);
        console.log('🔍 Auto-login result:', loginSuccess);
        
        if (loginSuccess) {
          setUser((current) => current ?? user);
          console.log('🔍 Registration and login completed successfully');
          return true;
        } else {
          console.warn('🔍 Auto-login failed after successful registration - user was created but not logged in');
          // Registration succeeded, but auto-login failed
          // This is not a critical error - user can login manually
          return true; // Return true because registration was successful
        }
      } else {
        const errorData = await response.json();
        console.error('🔍 Registration failed:', errorData);
        return false;
      }
    } catch (_error) {
      console.error('🔍 Registration error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string): Promise<{ success: boolean; message: string; errorCode?: string; suggestion?: string }> => {
    setIsLoading(true);
    
    try {
      const response = await fetch(apiUrl('/api/auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Password reset email sent:', data.message);
        return { success: true, message: data.message };
      } else {
        const errorData = await response.json();
        console.error('Forgot password failed:', errorData);
        
        // Handle specific error cases
        if (response.status === 404 && errorData.error_code === 'EMAIL_NOT_FOUND') {
          return { 
            success: false, 
            message: errorData.message, 
            errorCode: errorData.error_code,
            suggestion: errorData.suggestion 
          };
        }
        
        return { success: false, message: errorData.detail || 'Failed to send reset email' };
      }
    } catch (_error) {
      console.error('Forgot password error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyResetToken = async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(apiUrl('/api/auth/verify-reset-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.valid;
      } else {
        return false;
      }
    } catch (_error) {
      console.error('Token verification error:', error);
      return false;
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      const response = await fetch(apiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: newPassword
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Password reset successful:', data.message);
        return true;
      } else {
        const errorData = await response.json();
        console.error('Password reset failed:', errorData);
        return false;
      }
    } catch (_error) {
      console.error('Password reset error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    verifyResetToken,
    clearPasswordChangeFlag,
    verificationState,
    requestVerification,
    verifyCode,
    dismissVerificationPrompt,
    resetVerificationError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 
